/**
 * Trader portal shell. IBs share it (they are dual trader+IB profiles);
 * for them "Become an IB" turns into a shortcut to the partner portal.
 */
import { useMemo } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Wallet } from "lucide-react";
import { useMe } from "@/hooks/useAuth";
import { useFilteredTraderNav } from "@/hooks/useEnabledNav";
import { AppShell, ShellFooterCard, ShellSkeleton, orderBySection, type ShellNavItem } from "./AppShell";

const TRADER_SECTIONS: Record<string, string> = {
  "/trader/dashboard": "Overview",
  "/trader/accounts": "Trading",
  "/trader/web-trader": "Trading",
  "/trader/history": "Trading",
  "/trader/deposit": "Funds",
  "/trader/withdraw": "Funds",
  "/trader/fund-transfer": "Funds",
  "/trader/banks": "Funds",
  "/trader/copy-trading": "Grow",
  "/trader/funds": "Grow",
  "/trader/bonus": "Grow",
  "/trader/become-ib": "Grow",
  "/ib/dashboard": "Grow",
  "/trader/profile": "Account",
  "/trader/kyc": "Account",
  "/trader/account-settings": "Account",
  "/trader/support": "Account",
};
const ORDER = ["Overview", "Trading", "Funds", "Grow", "Account"];
const sectionOf = (i: ShellNavItem) => TRADER_SECTIONS[i.to ?? ""] ?? "More";

export function TraderShell() {
  const { data: me, isPending, isError } = useMe();
  const location = useLocation();
  const traderNav = useFilteredTraderNav();

  const nav = useMemo(() => {
    const items: ShellNavItem[] = traderNav.map((raw) =>
      me?.role === "ib" && raw.to === "/trader/become-ib"
        ? { ...raw, label: "Login as IB", to: "/ib/dashboard" }
        : { ...raw, end: raw.to === "/trader/dashboard" },
    );
    return orderBySection(items, sectionOf, ORDER);
  }, [traderNav, me?.role]);

  if (isPending) return <ShellSkeleton items={9} />;
  if (isError || !me) return <Navigate to="/login" replace state={{ from: location.pathname }} />;

  // Admin / platform_admin bounce to their own shells.
  if (me.role !== "trader" && me.role !== "ib" && me.role !== "affiliate") {
    if (me.role === "platform_admin") return <Navigate to="/platform/dashboard" replace />;
    if (me.role === "admin") return <Navigate to="/admin/dashboard" replace />;
    return <Navigate to="/login" replace />;
  }

  return (
    <AppShell
      nav={nav}
      sectionOf={sectionOf}
      storageKey="trader"
      maxWidth="max-w-[1440px]"
      footer={<ShellFooterCard icon={Wallet} title="Client Area" subtitle={me.fullName ?? me.email} />}
    />
  );
}
